import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useI18n } from "../i18n";
import { useAuth } from "../lib/auth";
import { prefs } from "../lib/prefs";
import { useStore } from "../lib/store";
import { Avatar } from "./Avatar";
import { Menu, Settings, User, Volume2, VolumeX } from "./Icon";
import { LangSwitch } from "./LangSwitch";
import { Logo } from "./Logo";
import { PrefsSheet } from "./PrefsSheet";
import { Sheet } from "./Sheet";

export function Header({ minimal }: { minimal?: boolean }) {
  const { d } = useI18n();
  const { user } = useAuth();
  const p = useStore(prefs);
  const [menu, setMenu] = useState(false);
  const [settings, setSettings] = useState(false);
  const [loc] = useLocation();
  const links = [
    { href: "/#games", label: d.nav.games },
    { href: "/joker", label: d.nav.joker },
    { href: "/join", label: d.nav.join },
  ];
  return (
    <header className="sticky top-0 z-40 bg-ground" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <div className="shell flex h-16 items-center gap-3">
        <Link href="~/" aria-label={d.nav.home} className="shrink-0">
          <Logo height={34} />
        </Link>
        {!minimal && (
          <nav className="ml-6 hidden items-center gap-1 md:flex" aria-label="main">
            {links.map((l) => (
              <Link
                key={l.href}
                href={`~${l.href}`}
                className={`rounded-full px-4 py-2 font-bold transition-colors hover:bg-ground-3 ${loc === l.href ? "text-ink" : "text-ink-2"}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
        )}
        <div className="ml-auto flex items-center gap-2">
          <div className="hidden sm:block">
            <LangSwitch compact />
          </div>
          <button
            className="icon-btn"
            aria-label={p.sound ? d.common.mute : d.common.unmute}
            aria-pressed={!p.sound}
            onClick={() => prefs.set((s) => ({ ...s, sound: !s.sound }))}
          >
            {p.sound ? <Volume2 size={21} /> : <VolumeX size={21} />}
          </button>
          <button className="icon-btn hidden sm:inline-grid" aria-label={d.prefs.title} onClick={() => setSettings(true)}>
            <Settings size={21} />
          </button>
          {!minimal && (
            <Link
              href={user ? "~/account" : "~/login"}
              className="hidden items-center gap-2 rounded-full bg-ground-3 py-1 pr-4 pl-1 font-bold hover:bg-ground-4 sm:flex"
            >
              {user ? <Avatar index={user.avatar ?? 0} size={36} /> : <span className="grid h-9 w-9 place-items-center rounded-full bg-ground-4"><User size={19} /></span>}
              <span className="max-w-[10ch] truncate">{user ? user.displayName : d.nav.login}</span>
            </Link>
          )}
          <button className="icon-btn sm:hidden" aria-label={d.nav.menu} onClick={() => setMenu(true)}>
            <Menu size={22} />
          </button>
        </div>
      </div>
      <Sheet open={menu} onClose={() => setMenu(false)} title={d.nav.menu} size="narrow">
        <nav className="flex flex-col gap-1" onClick={() => setMenu(false)}>
          {[...links, { href: user ? "/account" : "/login", label: user ? d.nav.account : d.nav.login }].map((l) => (
            <Link key={l.href} href={`~${l.href}`} className="rounded-2xl px-4 py-3 text-lg font-extrabold hover:bg-ground-3">
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="mt-4 space-y-3">
          <LangSwitch />
          <button className="chip" onClick={() => { setMenu(false); setSettings(true); }}>
            <Settings size={18} /> {d.prefs.title}
          </button>
        </div>
      </Sheet>
      <PrefsSheet open={settings} onClose={() => setSettings(false)} />
    </header>
  );
}
